// ---------------------------------------------------------------------
//  @ Role Permissions Service
// ---------------------------------------------------------------------

import { Injectable, inject } from '@angular/core';
import { RolePermissionsEndpoints } from 'app/core/enums/endpoints/role-permissions.enum';
import { Role } from 'app/core/security/interfaces';
import { ApiService } from 'app/services/api/api.service';
import { Observable } from 'rxjs';
import {
    RolePermissionRequest,
    RolePermissionResponse,
} from '../store/role-permissions.models';

@Injectable({
    providedIn: 'root',
})
export class RolePermissionsService {
    private _apiService = inject(ApiService);

    /**
     * Get all role permissions
     */
    getAll(): Observable<RolePermissionResponse> {
        return this._apiService.get(RolePermissionsEndpoints.getAll);
    }

    /**
     * Get role permissions by role
     */
    getByRole(role: Role): Observable<RolePermissionResponse> {
        return this._apiService.getWithParam(
            RolePermissionsEndpoints.getByRole,
            role
        );
    }

    /**
     * Get one role permission by ID
     */
    getOne(id: number): Observable<RolePermissionResponse> {
        return this._apiService.getOne(RolePermissionsEndpoints.getOne, id);
    }

    /**
     * Create a role permission
     */
    create(
        permission: RolePermissionRequest
    ): Observable<RolePermissionResponse> {
        return this._apiService.post(
            RolePermissionsEndpoints.create,
            permission
        );
    }

    /**
     * Bulk create role permissions
     */
    bulkCreate(
        permissions: RolePermissionRequest[]
    ): Observable<RolePermissionResponse> {
        return this._apiService.post(
            RolePermissionsEndpoints.bulkCreate,
            permissions
        );
    }

    /**
     * Update a role permission
     */
    update(
        id: number,
        permission: Partial<RolePermissionRequest>
    ): Observable<RolePermissionResponse> {
        return this._apiService.update(
            RolePermissionsEndpoints.patch,
            permission,
            id
        );
    }

    /**
     * Delete a role permission
     */
    delete(id: number): Observable<RolePermissionResponse> {
        return this._apiService.delete(RolePermissionsEndpoints.delete, id);
    }

    /**
     * Bulk delete role permissions
     */
    bulkDelete(ids: number[]): Observable<RolePermissionResponse> {
        return this._apiService.post(RolePermissionsEndpoints.bulkDelete, {
            ids,
        });
    }
}
